'use client';

import { useCallback, useEffect, useRef } from 'react';
import {
  connectorDraftKey,
  CONNECTOR_DRAFT_TTL_MS,
  sanitizeConnectorDraft,
} from '@/lib/integrations/setup-draft';

export function useConnectorDraft(
  userId: string | undefined,
  projectId: string | null | undefined,
  moduleId: string,
  value: Record<string, string>,
  restore: (value: Record<string, string>) => void
) {
  const key = userId && projectId ? connectorDraftKey(userId, projectId, moduleId) : null;
  const restoreRef = useRef(restore);
  restoreRef.current = restore;
  const skipWrite = useRef(false);
  const cleared = useRef<string | null>(null);
  useEffect(() => {
    skipWrite.current = true;
    cleared.current = null;
    if (!key) {
      restoreRef.current({});
      return;
    }
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) {
        restoreRef.current({});
        return;
      }
      const draft = JSON.parse(raw) as {
        version: number;
        expiresAt: number;
        value: Record<string, unknown>;
      };
      if (
        draft.version !== 1 ||
        !Number.isFinite(draft.expiresAt) ||
        draft.expiresAt <= Date.now()
      ) {
        sessionStorage.removeItem(key);
        restoreRef.current({});
        return;
      }
      restoreRef.current(sanitizeConnectorDraft(moduleId, draft.value));
    } catch {
      restoreRef.current({}); /* Setup works when storage is unavailable. */
    }
  }, [key, moduleId]);
  const serialized = JSON.stringify(sanitizeConnectorDraft(moduleId, value));
  const latestSerialized = useRef(serialized);
  latestSerialized.current = serialized;
  useEffect(() => {
    if (skipWrite.current) {
      skipWrite.current = false;
      return;
    }
    if (!key || cleared.current === serialized) return;
    cleared.current = null;
    try {
      sessionStorage.setItem(
        key,
        JSON.stringify({
          version: 1,
          expiresAt: Date.now() + CONNECTOR_DRAFT_TTL_MS,
          value: JSON.parse(serialized),
        })
      );
    } catch {
      /* Optional draft. */
    }
  }, [key, serialized]);
  return useCallback(
    (fields?: string[]) => {
      cleared.current = latestSerialized.current;
      try {
        if (key && fields) {
          const remaining = JSON.parse(latestSerialized.current);
          for (const field of fields) delete remaining[field];
          sessionStorage.setItem(
            key,
            JSON.stringify({
              version: 1,
              expiresAt: Date.now() + CONNECTOR_DRAFT_TTL_MS,
              value: remaining,
            })
          );
        } else if (key) sessionStorage.removeItem(key);
      } catch {
        /* Optional draft. */
      }
    },
    [key]
  );
}
